import { randomInt } from '@/webgl/math'
import { pickRandomItem, RandomPickEntryList } from '@/webgl/runner/core/Random'
import { CollectibleEntity } from '@/webgl/runner/entities/CollectibleEntity'
import { TrackEntity } from '@/webgl/runner/entities/TrackEntity'
import GameWorld from '@/webgl/runner/GameWorld'

const CHUNK_SIZE = 2

export type ShoppingBalanceTrackConfig = {
  type: 'shopping-balance'
}

export default class ShoppingBalanceTrack {
  frequency: number = 2.0
  variation: number = 2.0
  timeline: TrackEntity[] = []
  time: number = 0
  offset: number = 0

  constructor(
    readonly world: GameWorld,
    public cart: CollectibleEntity[]
  ) {}

  generateChunk(start: number, count: number) {
    if (this.cart.length === 0) {
      return
    }

    this.timeline = []

    const lanesPick: RandomPickEntryList<number> = [
      [0, 1],
      [1, 1],
      [2, 1],
    ]
    const randomPick: RandomPickEntryList<CollectibleEntity> = []
    const elligible = this.cart.filter((c) => !this.world.collectibles.includes(c))

    if (elligible.length == 0) return

    for (let i = 0; i < elligible.length; i++) {
      randomPick.push([elligible[i], 1])
    }

    let time = start
    for (let i = 0; i < count; i++) {
      if (randomPick.length == 0) break

      const entity = pickRandomItem(randomPick)

      time += this.frequency + Math.random() * this.variation

      const lane = pickRandomItem(lanesPick)
      lanesPick[lane][1] = Math.max(lanesPick[lane][1] - 0.5, 0.0)
      this.timeline.push({
        time,
        lane: lane - 1,
        data: entity.data,
        entity,
      })

      randomPick.splice(randomPick.findIndex((s) => s[0] == entity))
    }
  }

  injectBonus(id: string) {
    const time = this.time + 1.0
    this.offset = 1.0
    this.timeline = []
    this.timeline.push({ time, lane: randomInt(-1, 1), data: { id } })
  }

  injectBarrier(id: string) {
    const time = this.time + 1.0
    this.offset = 1.0
    this.timeline = []
    this.timeline.push({ time, lane: -1, data: { id } })
    this.timeline.push({ time, lane: 0, data: { id } })
    this.timeline.push({ time, lane: 1, data: { id } })
  }

  evaluate(deltaTime: number): TrackEntity[] {
    this.time += deltaTime
    if (this.timeline.length === 0) {
      this.generateChunk(this.time + this.offset, CHUNK_SIZE)
      this.offset = 0
    }

    const spawnQueue = []
    for (let i = this.timeline.length - 1; i >= 0; i--) {
      if (this.timeline[i].time < this.time) {
        spawnQueue.push(this.timeline[i])
        this.timeline.splice(i, 1)
      }
    }

    return spawnQueue
  }

  clear(): void {
    this.time = 0
    this.timeline = []
    this.cart = []
  }
}
